import { useEffect, useRef, useState } from "react";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import {
  archivedTabButtonProps,
  archivedTicketsActionMessages,
  fileViewerModalProps,
  getArchivedTicketColumns,
  getDeleteModalProps,
  getManagerReviewModalProps,
  mockData,
} from "./constant";
import { Button, message, Modal } from "antd";
import styles from "./ArchivedTickets.module.scss";
import TicketProcessingServices from "../../../services/ticketProcessing.service";
import { ArchivedTicketActionsEnum } from "../../../enums/TicketProcessingEnums";
import FileViewSection from "../DataInput/FileViewSection";
import { buildTicketUrl } from "../DataInput/constants";

const ArchivedTickets = ({
  archivedTabEditAccess,
}: {
  archivedTabEditAccess?: boolean;
}) => {
  const columns = getArchivedTicketColumns();
  const { FetchArchivedTickets, ArchivedTicketActions } =
    TicketProcessingServices();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const [unselectedRowKeys, setUnselectedRowKeys] = useState([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const {
    data: archivedTickets,
    refetch: refetchArchivedTicket,
    isFetching: isArchivedTicketsCallPending,
  } = FetchArchivedTickets({
    page: currentPage,
    perPage: pageSize,
  });

  const {
    mutateAsync: archivedTicketActions,
    isPending: isArchivedTicketActionPending,
  } = ArchivedTicketActions();

  const handleArchivedTicketsAction = async (
    action?: ArchivedTicketActionsEnum,
  ) => {
    const [ticketIds, isSelectAll, ticketAction] = [
      allPagesSelected ? unselectedRowKeys : selectedRowKeys,
      allPagesSelected,
      action,
    ];
    const ticketCount = isSelectAll
      ? archivedTickets?.data?.length - ticketIds.length
      : ticketIds.length;
    const archiveMessage = archivedTicketsActionMessages(ticketCount);
    await archivedTicketActions(
      {
        ticketProcessIds: ticketIds,
        unarchiveAllTickets: isSelectAll,
        action: ticketAction,
      },
      {
        onSuccess: async () => {
          message.success(
            ticketAction === ArchivedTicketActionsEnum.RESET
              ? archiveMessage.reset
              : archiveMessage.delete,
          );
          setSelectedRows([]);
          setSelectedRowKeys([]);
          setUnselectedRowKeys([]);
          if (allPagesSelected) {
            setAllPagesSelected(false);
          }
        },
        onError: () => {
          message.error(
            ticketAction === ArchivedTicketActionsEnum.RESET
              ? archiveMessage.resetError
              : archiveMessage.deleteError,
          );
        },
      },
    );
  };

  const handleReset = async () => {
    handleManagerReviewModalClose();
    await handleArchivedTicketsAction(ArchivedTicketActionsEnum.RESET);
  };

  const handleDelete = async () => {
    handleDeleteModalClose();
    await handleArchivedTicketsAction(ArchivedTicketActionsEnum.DELETE);
  };

  const handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
    if (allPagesSelected) {
      const currentPageKeys = (archivedTickets?.data || []).map(
        (row) => row.ticketId,
      );
      const currentPageUnselected = currentPageKeys.filter(
        (key) => !selectedRowKeys.includes(key),
      );

      const newUnselectedKeys = [...unselectedRowKeys];

      selectedRowKeys.forEach((key) => {
        const index = newUnselectedKeys.indexOf(key);
        if (index > -1) {
          newUnselectedKeys.splice(index, 1);
        }
      });

      currentPageUnselected.forEach((key) => {
        if (!newUnselectedKeys.includes(key)) {
          newUnselectedKeys.push(key);
        }
      });

      setUnselectedRowKeys(newUnselectedKeys);
    } else {
      setSelectedRows(selectedRows);
      setSelectedRowKeys(selectedRowKeys);
      setUnselectedRowKeys([]);
    }
  };

  const handleSelectAllPages = () => {
    setAllPagesSelected(true);
    setUnselectedRowKeys([]);
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const getEffectiveSelectedKeysForCurrentPage = () => {
    if (allPagesSelected) {
      const currentPageKeys = (archivedTickets?.data || []).map(
        (row) => row.ticketId,
      );
      return currentPageKeys.filter((key) => !unselectedRowKeys.includes(key));
    }
    return selectedRowKeys;
  };

  const getTotalSelectedCount = () => {
    if (allPagesSelected) {
      const totalRecords = archivedTickets?.meta?.totalRecords || 0;
      return totalRecords - unselectedRowKeys.length;
    }
    return selectedRows.length;
  };

  const getActualSelectedKeys = () => {
    if (allPagesSelected) {
      return { allSelected: true, unselectedKeys: unselectedRowKeys };
    }
    return { allSelected: false, selectedKeys: selectedRowKeys };
  };

  const handleDeselectAll = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setAllPagesSelected(false);
    setUnselectedRowKeys([]);
  };

  const [managerReviewButtonProp, deleteButtonProp] = [
    archivedTabButtonProps.managerReview,
    archivedTabButtonProps.delete,
  ];
  const deleteModalProps = getDeleteModalProps(getTotalSelectedCount());
  const managerReviewModalProps = getManagerReviewModalProps(
    getTotalSelectedCount(),
  );
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const handleDeleteModalOpen = () => {
    setDeleteModalVisible(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalVisible(false);
  };
  const [isManagerReviewModalVisible, setManagerReviewModalVisible] =
    useState(false);
  const handleManagerReviewModalOpen = () => {
    setManagerReviewModalVisible(true);
  };
  const handleManagerReviewModalClose = () => {
    setManagerReviewModalVisible(false);
  };

  const [ticketUploadUrl, setTicketUploadUrl] = useState({});
  const [isFileViewerModalVisible, setFileViewerModalVisible] = useState(false);
  const handlePdfPreviewOpen = () => {
    setFileViewerModalVisible(true);
  };
  const handlePdfPreviewClose = () => {
    setFileViewerModalVisible(false);
    setTicketUploadUrl({});
  };

  const handleViewArchievedTicket = (row) => {
    const fetchTicketUrls = async () => {
      const ticketProcessingLink = await buildTicketUrl(row);
      await setTicketUploadUrl(ticketProcessingLink);
    };
    fetchTicketUrls();
    handlePdfPreviewOpen();
  };

  const rowselection = {
    onChange: handleRowSelectionChange,
    preserveSelectedRowKeys: true,

    selectedRowKeys: getEffectiveSelectedKeysForCurrentPage(),
    selections: [
      {
        key: "select-all-pages",
        text: "Select All Pages",
        onSelect: handleSelectAllPages,
      },
      {
        key: "deselect-all",
        text: "Deselect All",
        onSelect: handleDeselectAll,
      },
    ],
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (allPagesSelected) {
        const currentPageKeys = (archivedTickets?.data || []).map(
          (row) => row.ticketId,
        );

        if (selected) {
          const newUnselectedKeys = unselectedRowKeys.filter(
            (key) => !currentPageKeys.includes(key),
          );
          setUnselectedRowKeys(newUnselectedKeys);
        } else {
          const newUnselectedKeys = [...unselectedRowKeys];
          currentPageKeys.forEach((key) => {
            if (!newUnselectedKeys.includes(key)) {
              newUnselectedKeys.push(key);
            }
          });
          setUnselectedRowKeys(newUnselectedKeys);
        }
      } else {
        if (selected) {
          const currentPageRows = archivedTickets?.data || [];
          const newSelectedKeys = [...selectedRowKeys];
          const newSelectedRows = [...selectedRows];

          currentPageRows.forEach((row) => {
            if (!newSelectedKeys.includes(row.ticketId)) {
              newSelectedKeys.push(row.ticketId);
              newSelectedRows.push(row);
            }
          });

          setSelectedRowKeys(newSelectedKeys);
          setSelectedRows(newSelectedRows);
        } else {
          const currentPageKeys = (archivedTickets?.data || []).map(
            (row) => row.ticketId,
          );
          const newSelectedKeys = selectedRowKeys.filter(
            (key) => !currentPageKeys.includes(key),
          );
          const newSelectedRows = selectedRows.filter(
            (row) => !currentPageKeys.includes(row.ticketId),
          );

          setSelectedRowKeys(newSelectedKeys);
          setSelectedRows(newSelectedRows);
        }
      }
    },
  };

  useEffect(() => {
    refetchArchivedTicket();
  }, [currentPage, pageSize]);

  useEffect(() => {}, [currentPage, archivedTickets]);

  const getSelectionForAPI = () => {
    const selectionData = getActualSelectedKeys();
    return selectionData;
  };

  return (
    <>
      <Modal
        title={managerReviewModalProps.title}
        width={managerReviewModalProps.width}
        okText={managerReviewModalProps.okButtonLabel}
        cancelText={managerReviewModalProps.cancelButtonLabel}
        visible={isManagerReviewModalVisible}
        onCancel={handleManagerReviewModalClose}
        onOk={handleReset}
        focusTriggerAfterClose={managerReviewModalProps.focusTriggerAfterClose}
      >
        {managerReviewModalProps.content}
      </Modal>
      <Modal
        title={deleteModalProps.title}
        width={deleteModalProps.width}
        okText={deleteModalProps.okButtonLabel}
        cancelText={deleteModalProps.cancelButtonLabel}
        visible={isDeleteModalVisible}
        onCancel={handleDeleteModalClose}
        onOk={handleDelete}
        focusTriggerAfterClose={deleteModalProps.focusTriggerAfterClose}
      >
        {deleteModalProps.content}
      </Modal>
      {allPagesSelected && (
        <div className={styles.SelectionInfo}>
          {getTotalSelectedCount()} of {archivedTickets?.meta?.totalRecords}{" "}
          tickets selected across all pages
          {unselectedRowKeys.length > 0 &&
            ` (${unselectedRowKeys.length} unselected)`}
        </div>
      )}
      <Modal
        title={fileViewerModalProps.title}
        width={fileViewerModalProps.width}
        footer={fileViewerModalProps.footer}
        visible={isFileViewerModalVisible}
        onCancel={handlePdfPreviewClose}
      >
        <div ref={wrapperRef} className={styles.mainFileView}>
          <FileViewSection
            url={ticketUploadUrl?.url ?? ""}
            wrapperRef={wrapperRef}
          />
        </div>
      </Modal>
      <div className={styles.ButtonWrapper}>
        <div className={styles.ButtonContainer}>
          <Button
            type={managerReviewButtonProp.type}
            disabled={getTotalSelectedCount() === 0 || !archivedTabEditAccess}
            onClick={handleManagerReviewModalOpen}
          >
            {managerReviewButtonProp.label}
          </Button>
          <Button
            type={deleteButtonProp.type}
            danger
            disabled={getTotalSelectedCount() === 0 || !archivedTabEditAccess}
            onClick={handleDeleteModalOpen}
          >
            {deleteButtonProp.label}
          </Button>
        </div>
      </div>
      <CustomTableWithPagination
        isClickableTable
        dataSource={archivedTickets?.data}
        columns={columns}
        pageSize={pageSize}
        currentPage={currentPage}
        rowSelection={rowselection}
        rowKey={(record) => record?.ticketId}
        className={styles.ArchiveTable}
        loading={isArchivedTicketsCallPending}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        handleSelectedRow={handleViewArchievedTicket}
        totalData={archivedTickets?.meta?.totalRecords}
      />
    </>
  );
};

export default ArchivedTickets;
